/** One pluggable section of the Settings modal. Each section owns its DOM under the
 *  provided parent element and is responsible for its own persistence. The modal just
 *  registers sections, builds the nav, and shows the selected one. */
export interface SettingsSection {
  id: string;
  title: string;
  build(parent: HTMLElement): void;
}

/** General settings overlay. Sections register via {@link addSection}; today there's
 *  one (Map → Area notifications) but the structure is the seed of a full settings hub
 *  — audio, controls, graphics, etc. drop in as additional sections without refactor.
 *
 *  Open/close: click ⚙ button, press Esc, click backdrop, or call {@link toggle} / {@link open} / {@link close}. */
export class SettingsModal {
  private button: HTMLButtonElement;
  private backdrop: HTMLElement;
  private modal: HTMLElement;
  private titleEl: HTMLElement;
  private navEl: HTMLElement;
  private bodyEl: HTMLElement;
  private sections: SettingsSection[] = [];
  private activeId: string | null = null;
  private boundKeydown: (e: KeyboardEvent) => void;

  constructor(button: HTMLButtonElement) {
    this.button = button;

    this.backdrop = document.createElement('div');
    this.backdrop.className = 'settings-modal-backdrop';
    this.backdrop.hidden = true;

    this.modal = document.createElement('div');
    this.modal.className = 'settings-modal';
    this.modal.setAttribute('role', 'dialog');
    this.modal.setAttribute('aria-modal', 'true');
    this.modal.setAttribute('aria-label', 'Settings');

    const header = document.createElement('div');
    header.className = 'settings-modal-header';
    this.titleEl = document.createElement('div');
    this.titleEl.className = 'settings-modal-title';
    this.titleEl.textContent = 'Settings';
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'ghost settings-modal-close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', () => this.close());
    header.appendChild(this.titleEl);
    header.appendChild(closeBtn);

    const main = document.createElement('div');
    main.className = 'settings-modal-main';
    this.navEl = document.createElement('div');
    this.navEl.className = 'settings-section-nav';
    this.bodyEl = document.createElement('div');
    this.bodyEl.className = 'settings-section-body';
    main.appendChild(this.navEl);
    main.appendChild(this.bodyEl);

    this.modal.appendChild(header);
    this.modal.appendChild(main);
    this.backdrop.appendChild(this.modal);
    document.body.appendChild(this.backdrop);

    // Stop clicks inside the modal from bubbling to the backdrop (which closes).
    this.modal.addEventListener('click', (e) => e.stopPropagation());
    this.backdrop.addEventListener('click', () => this.close());

    this.button.addEventListener('click', () => this.toggle());

    // Esc + O (toggle) shortcuts — registered once, ignored while a text input is focused
    // so the user can type without the modal stealing keys.
    this.boundKeydown = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const inTextField = !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA');
      if (e.key === 'Escape' && !this.backdrop.hidden) {
        this.close();
        return;
      }
      if ((e.key === 'o' || e.key === 'O') && !inTextField) {
        this.toggle();
      }
    };
    window.addEventListener('keydown', this.boundKeydown);
  }

  addSection(section: SettingsSection): void {
    this.sections.push(section);
    this.rebuildNav();
    if (this.activeId === null) this.activeId = section.id;
  }

  open(): void {
    if (!this.backdrop.hidden) return;
    this.backdrop.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
    if (this.activeId !== null) this.showSection(this.activeId);
  }

  close(): void {
    if (this.backdrop.hidden) return;
    this.backdrop.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
  }

  toggle(): void {
    if (this.backdrop.hidden) this.open();
    else this.close();
  }

  private rebuildNav(): void {
    // Hide the nav rail entirely when there's only one section — no point in a list of one.
    if (this.sections.length < 2) {
      this.navEl.replaceChildren();
      this.navEl.hidden = true;
      return;
    }
    this.navEl.hidden = false;
    this.navEl.replaceChildren();
    for (const s of this.sections) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'settings-nav-item';
      btn.textContent = s.title;
      btn.dataset.section = s.id;
      btn.addEventListener('click', () => this.showSection(s.id));
      this.navEl.appendChild(btn);
    }
  }

  private showSection(id: string): void {
    const section = this.sections.find((s) => s.id === id);
    if (!section) return;
    this.activeId = id;
    // Update nav highlight.
    for (const child of Array.from(this.navEl.children)) {
      const el = child as HTMLElement;
      el.classList.toggle('active', el.dataset.section === id);
    }
    this.bodyEl.replaceChildren();
    section.build(this.bodyEl);
  }
}

// Section factories live in `./settings/*Section.ts` — import them and call
// `modal.addSection(...)` from main.ts.
